import {
  ActionError,
  Button,
  Field,
  FormBlock,
  Input,
  MoneyInput,
  PageHeader,
  PageTitle,
  ReadingColumn,
  StepProgress,
  toast,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { Page } from '@/app/shell'
import type { CatalogProcedureOption } from '@/features/catalog/api'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { routes } from '@/routes'
import { useCreateProcedures, type PaymentTerms, type ProcedureInput } from './api'
import { CatalogPicker } from './catalog-picker'
import { EMPTY_PAYMENT_TERMS } from './payment-terms'
import { PaymentTermsFields } from './payment-terms-fields'

/**
 * Adicionar vários procedimentos de uma vez (docs/areas/administracao.md, "Adicionar vários de uma
 * vez"; RN-ADM-76). Arquétipo: fluxo em etapas. É a etapa 3 da implantação e o caminho de quem
 * monta o catálogo do zero: escolhe na lista da Westhetics, depois dá valor e pagamento.
 */

interface Row {
  id: string
  name: string
  amountCents: number | null
}

export function AddProceduresPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // Vindo da implantação, concluir volta para a etapa Procedimentos dela.
  const fromOnboarding = params.get('from') === 'onboarding'
  const back = fromOnboarding ? routes.onboardingStep('procedures') : routes.admin.procedures

  const [step, setStep] = useState<1 | 2>(1)
  const [rows, setRows] = useState<Row[]>([])
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms>(EMPTY_PAYMENT_TERMS)
  const [discountText, setDiscountText] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const create = useCreateProcedures()

  function choose(_ids: string[], options: CatalogProcedureOption[]) {
    setRows((current) =>
      options.map(
        (option) =>
          current.find((row) => row.id === option.id) ?? {
            id: option.id,
            name: option.name,
            amountCents: null,
          },
      ),
    )
  }

  const updateRow = (id: string, patch: Partial<Row>) =>
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)))

  const rowError = (row: Row) => {
    const name = row.name.trim()
    if (name.length < 2) return 'Dê um nome ao procedimento.'
    if (name.length > 80) return 'O nome pode ter no máximo 80 caracteres.'
    if (name.includes(',')) return 'Sem vírgulas: elas separam procedimentos no documento gerado.'
    if (row.amountCents === null) return 'Informe um valor em reais.'
    return null
  }
  const methodsError =
    paymentTerms.methods.length === 0 ? 'Escolha pelo menos um meio de pagamento.' : undefined
  const discountError =
    discountText.trim() !== '' && !paymentTerms.upfrontDiscount
      ? 'Informe o desconto em porcentagem, como 10 ou 7,5.'
      : undefined
  const isValid = rows.every((row) => !rowError(row)) && !methodsError && !discountError
  const serverFields = fieldErrors(create.error)

  function submit() {
    setSubmitted(true)
    if (!isValid) return
    const procedures: ProcedureInput[] = rows.map((row) => ({
      name: row.name.trim(),
      description: null,
      items: [{ catalogProcedureId: row.id, quantity: 1 }],
      validityMonths: null,
      amountCents: row.amountCents ?? 0,
      paymentTerms,
    }))
    create.mutate(procedures, {
      onSuccess: (result) => {
        toast(
          result.createdCount === 1
            ? '1 procedimento adicionado ao catálogo'
            : `${result.createdCount} procedimentos adicionados ao catálogo`,
        )
        navigate(back)
      },
    })
  }

  const count = rows.length

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          fromOnboarding
            ? { label: 'Implantação', href: routes.onboardingStep('procedures') }
            : { label: 'Procedimentos', href: routes.admin.procedures },
          { label: 'adicionar' },
        ]}
      />
      <Page>
        <ReadingColumn wide>
          <StepProgress
            current={step}
            total={2}
            label={step === 1 ? 'Escolha os procedimentos' : 'Valor e pagamento'}
          />
          <PageTitle
            className="mt-6"
            title={step === 1 ? 'O que a clínica oferece' : 'Quanto custa e como se paga'}
            subtitle={
              step === 1
                ? 'A lista é da Westhetics: cada procedimento dela já vem com o termo de consentimento revisado. Cada escolhido vira um procedimento da clínica; para juntar vários num só, use Novo procedimento.'
                : 'O nome é o que a paciente lê no contrato; pode ser o que a clínica já usa. A condição de pagamento vale para todos e pode ser ajustada depois, um a um.'
            }
          />

          {step === 1 ? (
            <>
              <div className="mt-6">
                <CatalogPicker
                  multiple
                  disableInCatalog
                  autoFocus
                  value={rows.map((row) => row.id)}
                  onChange={choose}
                />
              </div>
              <div className="mt-6 flex flex-wrap justify-end gap-3">
                <Button variant="outline" asChild>
                  <Link to={back}>Voltar</Link>
                </Button>
                <Button disabled={count === 0} onClick={() => setStep(2)}>
                  {count === 0 ? 'Selecione um procedimento' : `Avançar com ${count}`}
                </Button>
              </div>
            </>
          ) : (
            <>
              {create.isError ? (
                <ActionError
                  className="mt-6"
                  title="Nenhum procedimento foi adicionado"
                  description={
                    Object.keys(serverFields).length > 0
                      ? Object.values(serverFields).join(' ')
                      : errorMessage(create.error)
                  }
                />
              ) : null}

              <FormBlock
                className="mt-6"
                title="Condição de pagamento padrão"
                note="A frase do contrato é gerada, não digitada, e fica igual em todo documento."
              >
                <PaymentTermsFields
                  value={paymentTerms}
                  onChange={setPaymentTerms}
                  discountText={discountText}
                  onDiscountText={setDiscountText}
                  errors={
                    submitted ? { methods: methodsError, upfrontDiscount: discountError } : {}
                  }
                />
              </FormBlock>

              <FormBlock
                className="mt-4"
                title="Procedimentos"
                note="Nome e valor de cada um, como vão aparecer no contrato."
              >
                <div className="flex flex-col gap-4">
                  {rows.map((row) => {
                    const error = submitted ? rowError(row) : null
                    const isNameError = Boolean(error) && !error?.startsWith('Informe um valor')
                    return (
                      <div
                        key={row.id}
                        className="tablet:grid-cols-[minmax(0,1fr)_180px] grid grid-cols-1 gap-x-5 gap-y-3"
                      >
                        <Field label="Nome" error={isNameError ? error : null}>
                          <Input
                            value={row.name}
                            maxLength={120}
                            onChange={(event) => updateRow(row.id, { name: event.target.value })}
                          />
                        </Field>
                        <Field label="Valor" error={error && !isNameError ? error : null}>
                          <MoneyInput
                            value={row.amountCents}
                            onChange={(amountCents) => updateRow(row.id, { amountCents })}
                          />
                        </Field>
                      </div>
                    )
                  })}
                </div>
              </FormBlock>

              <div className="mt-6 flex flex-wrap justify-end gap-3">
                <Button variant="outline" onClick={() => setStep(1)}>
                  Voltar
                </Button>
                <Button
                  onClick={submit}
                  loading={create.isPending}
                  loadingLabel="Adicionando ao catálogo…"
                >
                  {count === 1 ? 'Adicionar 1 procedimento' : `Adicionar ${count} procedimentos`}
                </Button>
              </div>
            </>
          )}
        </ReadingColumn>
      </Page>
    </>
  )
}
